import React from 'react'
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Box, 
  FormControl, 
  FormLabel, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Checkbox,
  Divider,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WorkIcon from '@mui/icons-material/Work'
import SchoolIcon from '@mui/icons-material/School'
import DescriptionIcon from '@mui/icons-material/Description'
import { ACADEMIC_ENUM, AREA_ENUM, VISA_ENUM } from 'data/enums'

// Details scope options
const DETAILS_SCOPE = {
  HOSPITAL_WIDE: 'hospital-wide',
  CAMPUS_SPECIFIC: 'campus_specific',
  JOB_SPECIFIC: 'job_specific'
}

// Details configuration
const details = {
  pslfCompatible: {
    label: 'PSLF Compatible',
    icon: <DescriptionIcon />,
    description: 'Public Service Loan Forgiveness eligibility'
  },
  loanForgiveness: {
    label: 'Loan Forgiveness',
    icon: <DescriptionIcon />,
    description: 'Other loan forgiveness programs'
  },
  visaSponsorship: {
    label: 'Visa Sponsorship',
    icon: <DescriptionIcon />,
    description: 'Visa sponsorship availability',
    type: 'select',
    options: Object.entries(VISA_ENUM).map(([id, name]) => ({
      value: id,
      label: name
    }))
  },
  academicType: {
    label: 'Academic Type',
    icon: <SchoolIcon />,
    description: 'Academic appointment type',
    type: 'select',
    options: Object.entries(ACADEMIC_ENUM).map(([id, name]) => ({
      value: id,
      label: name
    }))
  },
  area: {
    label: 'Area Type',
    icon: <LocationOnIcon />,
    description: 'Geographic area or region',
    type: 'select',
    options: Object.entries(AREA_ENUM).map(([id, name]) => ({
      value: id,
      label: name
    }))
  }
}

const Details = ({ 
  jobDetails,
  handleDetailsChange,
  handleDetailsSubmit,
  setConfigStep,
  CONFIG_STEPS,
  campuses = [] // Add campuses prop with default empty array
}) => {
  // Helper function to get the icon for each detail
  const getDetailIcon = (key) => {
    return details[key]?.icon || <DescriptionIcon />
  }

  // Helper function to get the description for each detail
  const getDetailDescription = (key) => {
    return details[key]?.description || ''
  }

  // Helper function to get the label for each detail
  const getDetailLabel = (key) => {
    return details[key]?.label || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
  }

  const renderField = (key, field) => {
    if (field.type === 'select') {
      return (
        <FormControl fullWidth>
          <FormLabel>{field.label}</FormLabel>
          <Select
            value={jobDetails.details[key]?.value || ''}
            onChange={(e) => handleDetailsChange(key, 'value', e.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            {field.options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{field.description}</FormHelperText>
        </FormControl>
      )
    }

    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={jobDetails.details[key]?.value || false}
            onChange={(e) => handleDetailsChange(key, 'value', e.target.checked)}
          />
        }
        label={
          <Box>
            <Typography variant="body1">{field.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {field.description}
            </Typography>
          </Box>
        }
      />
    )
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'medium', mb: 3 }}>
          Job Details Configuration
        </Typography>
        
        <Typography variant="body1" paragraph sx={{ mb: 3, color: 'text.secondary' }}>
          Configure the details for your job listings. You can set these details at different scopes:
          hospital-wide, campus-specific, or job-specific.
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
            Scope Options
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Hospital-wide</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Apply the same value to all campuses and jobs
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Campus-specific</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Set different values for each campus
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <WorkIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Job-specific</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Set values individually when creating jobs
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {Object.entries(jobDetails.details).map(([key, detail]) => (
          <Box key={key} sx={{ mb: 4 }}>
            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getDetailIcon(key)}
                <Box sx={{ ml: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    {getDetailLabel(key)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getDetailDescription(key)}
                  </Typography>
                </Box>
              </Box>
              
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend">Application Scope</FormLabel>
                <RadioGroup
                  row
                  value={detail.scope}
                  onChange={(e) => {
                    const newScope = e.target.value
                    // If changing to job-specific, clear the value
                    if (newScope === DETAILS_SCOPE.JOB_SPECIFIC) {
                      handleDetailsChange(key, 'value', undefined)
                    }
                    handleDetailsChange(key, 'scope', newScope)
                  }}
                >
                  <FormControlLabel 
                    value={DETAILS_SCOPE.HOSPITAL_WIDE} 
                    control={<Radio />} 
                    label="Hospital-wide" 
                  />
                  {campuses.length > 0 && (
                    <FormControlLabel 
                      value={DETAILS_SCOPE.CAMPUS_SPECIFIC} 
                      control={<Radio />} 
                      label="Campus-specific" 
                    />
                  )}
                  <FormControlLabel 
                    value={DETAILS_SCOPE.JOB_SPECIFIC} 
                    control={<Radio />} 
                    label="Job-specific" 
                  />
                </RadioGroup>
              </FormControl>
              
              {/* Hospital-wide value input */}
              {detail.scope === DETAILS_SCOPE.HOSPITAL_WIDE && (
                <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Hospital-wide Value
                  </Typography>
                  {renderField(key, details[key])}
                </Box>
              )}
              
              {/* Campus-specific values */}
              {detail.scope === DETAILS_SCOPE.CAMPUS_SPECIFIC && campuses.length > 0 && (
                <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Set values for each campus
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {campuses.map((campus) => (
                      <Grid item xs={12} sm={6} key={campus.id}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {campus.name || 'Unnamed Campus'}
                          </Typography>
                          {renderField(key, details[key])}
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              
              {/* Job-specific message */}
              {detail.scope === DETAILS_SCOPE.JOB_SPECIFIC && (
                <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                  <Typography variant="body2" color="text.secondary">
                    This value will be set individually when creating each job position.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        ))}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button 
            variant="outlined" 
            onClick={() => setConfigStep(CONFIG_STEPS.CAMPUSES)}
          >
            Back
          </Button>
          <Button 
            variant="contained" 
            onClick={handleDetailsSubmit}
          >
            Continue
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export default Details 